import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Phone, Award, Briefcase, GraduationCap, MessageCircle, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const TeamPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const teamMembers = [
    {
      name: 'Nok',
      title: 'Experienced Attorney',
      subtitle: 'LL.B. | LL.M.',
      role: 'Thai Barrister-at-Law',
      specialty: 'Real Estate, Tax, & Corporate Legal Advisor for Foreigners',
      image: 'https://customer-assets.emergentagent.com/job_thailandrealty/artifacts/0esziq02_ChatGPT%20Image%20Mar%203%2C%202026%2C%2005_32_50%20PM.png',
      phone: '094-249-1546',
      lineUrl: 'https://lin.ee/soB6jcI',
      lineLabel: 'LINE — Legal',
      credentials: [
        'Bachelor of Laws (LL.B.)',
        'Master of Laws (LL.M.)',
        'Barrister-at-Law (Thailand)',
        'Licensed Attorney'
      ],
      expertise: [
        'Real Estate Transactions',
        'Tax Planning & Compliance',
        'Corporate Law',
        'Foreign Investment Advisory'
      ]
    },
    {
      name: 'Pook',
      title: 'AIA Life Insurance Agent',
      subtitle: 'Insurance & Wealth Protection Advisor',
      role: 'AIA Thailand — MDRT Member',
      specialty: 'Financial & Investment Planning',
      image: 'https://customer-assets.emergentagent.com/job_98495428-5c53-48f5-8d26-c8cbe5905854/artifacts/br6x2frh_Pook%202.jpg',
      phone: '081-941-5945',
      lineUrl: 'https://page.line.me/296nejkj',
      lineLabel: null,
      credentials: [
        'Life Insurance Agent and Non-Life Insurance Broker',
        'MDRT (Million Dollar Round Table) Member',
        'Financial Planning Specialist',
        'Investment Advisory Certification'
      ],
      expertise: [
        'Life Insurance',
        'Investment Planning',
        'Health Insurance for Retirees',
        'Long-Term Wealth Protection'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="team-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </Link>
            <Button onClick={() => navigate('/')} variant="ghost" className="text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-full" data-testid="back-to-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" data-testid="team-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="overline text-[#D4AF37] mb-4">{t('our_professionals')}</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
            {t('meet_our_team_title')}
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto leading-relaxed">
            {t('team_desc')}
          </p>
        </div>
      </section>

      {/* Team Members */}
      <section className="px-6 lg:px-8 pb-24" data-testid="team-grid">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className="property-card bg-[#131A38] rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/30"
              data-testid={`team-card-${member.name.toLowerCase()}`}
            >
              {/* Photo */}
              <div className="relative h-[420px] bg-[#0A0E27] overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131A38] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-4xl font-semibold text-[#D4AF37] mb-1">{member.name}</h3>
                  <p className="text-white/70 text-sm">{member.subtitle}</p>
                </div>
              </div>

              {/* Details */}
              <div className="p-7">
                <div className="mb-5">
                  <p className="text-xl font-semibold text-white mb-1">{member.title}</p>
                  <p className="text-[#94A3B8] text-sm flex items-center gap-2">
                    <Briefcase className="w-3.5 h-3.5 text-[#D4AF37]/60" />
                    {member.role}
                  </p>
                </div>

                <div className="mb-5 p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-xl">
                  <p className="text-[#94A3B8] text-sm leading-relaxed">
                    <strong className="text-[#D4AF37]">{t('specialty')}:</strong> {member.specialty}
                  </p>
                </div>

                {/* Credentials */}
                <div className="mb-5">
                  <h4 className="flex items-center gap-2 text-sm font-medium text-[#D4AF37] mb-3">
                    <GraduationCap className="w-4 h-4" />
                    {t('credentials')}
                  </h4>
                  <ul className="space-y-1.5">
                    {member.credentials.map((cred, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[#94A3B8] text-sm">
                        <span className="text-[#D4AF37] mt-0.5 text-xs">&#10003;</span>
                        <span>{cred}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Expertise */}
                <div className="mb-6">
                  <h4 className="flex items-center gap-2 text-sm font-medium text-[#D4AF37] mb-3">
                    <Award className="w-4 h-4" />
                    {t('expertise')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {member.expertise.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-[#94A3B8]">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact */}
                <div className="pt-5 border-t border-white/5 space-y-3">
                  <a
                    href={`tel:${member.phone}`}
                    className="flex items-center justify-center gap-3 w-full py-3 bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full transition-colors duration-300 text-sm"
                    data-testid={`call-${member.name.toLowerCase()}`}
                  >
                    <Phone className="w-4 h-4" />
                    {member.phone}
                  </a>
                  {member.lineUrl && (
                    <a
                      href={member.lineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 w-full py-3 bg-[#06C755] hover:bg-[#05b34d] text-white font-semibold rounded-full transition-colors duration-300 text-sm"
                      data-testid={`line-${member.name.toLowerCase()}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      {member.lineLabel || t('line_official')}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
